/**
 * Admin Layout Component
 *
 * Sidebar-based navigation layout for all admin pages.
 * Groups pages into logical sections with collapsible groups.
 * Responsive: sidebar on desktop, hamburger menu on mobile.
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  exact: boolean;
  icon: React.ReactNode;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// Simple 16x16 SVG icons
const icons = {
  dashboard: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1" />
      <rect x="9" y="1" width="6" height="6" rx="1" />
      <rect x="1" y="9" width="6" height="6" rx="1" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  ),
  collections: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <path d="M2 6h12M6 2v12" />
    </svg>
  ),
  projects: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h12M2 8h12M2 12h8" />
    </svg>
  ),
  trades: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 2l-3 3 3 3M12 8l3 3-3 3M1 5h14M1 11h14" />
    </svg>
  ),
  bundles: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="10" height="10" rx="1" />
      <path d="M3 7h10M7 3v10" />
    </svg>
  ),
  offers: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z" />
    </svg>
  ),
  marketplaces: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 6V14h12V6M1 2h14v4H1z" />
      <path d="M6 10h4v4H6z" />
    </svg>
  ),
  launches: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1L5 8h6L8 1zM6 8l-2 7h2l1-3h2l1 3h2L10 8H6z" />
    </svg>
  ),
  settlements: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4v4l3 2" />
    </svg>
  ),
  analytics: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 14V8M6 14V4M10 14V6M14 14V2" />
    </svg>
  ),
  transactions: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h12M2 8h12M2 12h12" />
      <circle cx="4" cy="4" r="1" fill="currentColor" />
      <circle cx="4" cy="8" r="1" fill="currentColor" />
      <circle cx="4" cy="12" r="1" fill="currentColor" />
    </svg>
  ),
  ledgerHealth: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 8h3l2-5 2 10 2-5h5" />
    </svg>
  ),
  notifications: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 1a4 4 0 00-4 4v3l-2 2v1h12v-1l-2-2V5a4 4 0 00-4-4zM6 13a2 2 0 004 0" />
    </svg>
  ),
  users: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="5" r="3" />
      <path d="M2 15a6 6 0 0112 0" />
    </svg>
  ),
  menu: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
      <path d="M3 5h14M3 10h14M3 15h14" />
    </svg>
  ),
  close: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  ),
};

const navGroups: NavGroup[] = [
  {
    label: 'Platform',
    items: [
      { to: '/admin', label: 'Dashboard', exact: true, icon: icons.dashboard },
    ],
  },
  {
    label: 'Content',
    items: [
      { to: '/admin/collections', label: 'Collections', exact: false, icon: icons.collections },
      { to: '/admin/projects', label: 'Projects', exact: false, icon: icons.projects },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { to: '/admin/trades', label: 'Trades', exact: false, icon: icons.trades },
      { to: '/admin/bundles', label: 'Bundles', exact: false, icon: icons.bundles },
      { to: '/admin/offers', label: 'Collection Offers', exact: false, icon: icons.offers },
      { to: '/admin/marketplace-activity', label: 'Marketplace Activity', exact: true, icon: icons.marketplaces },
      { to: '/admin/marketplaces', label: 'Marketplaces', exact: true, icon: icons.marketplaces },
    ],
  },
  {
    label: 'Launchpad',
    items: [
      { to: '/admin/launches', label: 'Launches', exact: false, icon: icons.launches },
      { to: '/admin/settlements', label: 'Settlements', exact: true, icon: icons.settlements },
      { to: '/admin/referral-payouts', label: 'Referral Payouts', exact: true, icon: icons.settlements },
      { to: '/admin/ambassadors', label: 'Ambassadors', exact: false, icon: icons.users },
      { to: '/admin/launchpad-analytics', label: 'Analytics', exact: true, icon: icons.analytics },
      { to: '/admin/launchpad-health', label: 'Health', exact: true, icon: icons.ledgerHealth },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/transactions', label: 'Transactions', exact: true, icon: icons.transactions },
      { to: '/admin/failed-transactions', label: 'Failed Transactions', exact: true, icon: icons.transactions },
      { to: '/admin/ledger-recovery', label: 'Stream Continuity', exact: true, icon: icons.ledgerHealth },
      { to: '/admin/ledger-health', label: 'Ledger Health', exact: true, icon: icons.ledgerHealth },
      { to: '/admin/notifications', label: 'Notifications', exact: true, icon: icons.notifications },
    ],
  },
  {
    label: 'Users',
    items: [
      { to: '/admin/users', label: 'All Users', exact: false, icon: icons.users },
    ],
  },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActiveLink = (to: string, exact: boolean) => {
    if (exact) return location.pathname === to;
    // For non-exact matches, ensure we don't match /admin/launches when on /admin/launchpad-analytics
    if (to === '/admin/launches') {
      return location.pathname.startsWith('/admin/launches');
    }
    return location.pathname.startsWith(to);
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex h-14 items-center px-4 border-b border-gray-700 flex-shrink-0">
        <Link to="/admin" className="text-lg font-bold text-white tracking-tight">
          XRP.deals Admin
        </Link>
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-4">
            <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {group.label}
            </h3>
            {group.items.map((item) => {
              const active = isActiveLink(item.to, item.exact);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                    active
                      ? 'bg-gray-800 text-white border-l-2 border-blue-500 -ml-px'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-700">
        <Link
          to="/"
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          Back to site
        </Link>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - mobile (overlay) */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-gray-900 flex flex-col transform transition-transform lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
        >
          {icons.close}
        </button>
        {sidebarContent}
      </div>

      {/* Sidebar - desktop (always visible) */}
      <div className="hidden lg:flex lg:w-56 lg:flex-col lg:flex-shrink-0">
        <div className="flex flex-col h-full bg-gray-900">
          {sidebarContent}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center h-12 px-4 bg-white border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900 mr-3"
          >
            {icons.menu}
          </button>
          <span className="text-sm font-semibold text-gray-900">Admin</span>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
